// Global
import dynamic from 'next/dynamic';
import classnames from 'classnames';

/**
 * Standardize SVG icons on a 48x48 grid to allow
 * for consistent use across the project
 *
 * Icon contents should be stored in the icons subdirectory
 * using the naming scheme 'icon--[name].tsx'
 */

type SvgIconSize = 'xxs' | 'xs' | 'sm' | 'md' | 'em' | 'em-lg';

type SvgViewBoxSize = '0 0 16 16' | '0 0 24 24' | '0 0 48 48';

export type SvgIconOption =
  | 'new-tab'
  | 'arrow-up'
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'filter'
  | 'calendar'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'quantity-minus'
  | 'quantity-plus'
  | 'error'
  | 'close'
  | 'info';

interface SvgIconProps {
  className?: string;
  icon?: SvgIconOption;
  size?: SvgIconSize;
  viewBoxSize?: SvgViewBoxSize;
}

const sizeClasses: Record<SvgIconSize, string> = {
  xxs: 'h-5 w-5',
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-16 w-16',
  em: 'h-em w-em',
  'em-lg': 'h-em w-em',
};

const SvgIcon = ({ icon, size = 'em', className, viewBoxSize }: SvgIconProps): JSX.Element => {
  const IconContent = dynamic(() => import(`./icons/icon--${icon}`));
  const variantClasses = size ? sizeClasses[size] : sizeClasses['sm'];
  return (
    <div
      className={classnames('svg-icon', 'inline-flex', 'align-middle', variantClasses, className)}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBoxSize}>
        <IconContent />
      </svg>
    </div>
  );
};

export default SvgIcon;
